import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ArrowLeft, Cpu, Smartphone, HeadphonesIcon } from 'lucide-react';
import { useQuery } from 'react-query';
import { serviceService } from '../../../services/serviceService';

const ServiceDetail = () => {
  const { slug } = useParams();
  const contentRef = useRef(null);
  const [currentIcon, setCurrentIcon] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  const fallbackSections = useMemo(
    () => [
      {
        slug: 'device-manufacturing',
        label: 'Device Manufacturing',
        heading: "Hardware Solutions",
        image: '/assets/images/support2.jpg',
        fullContent:
          "SiWi's hardware engineering capabilities span the entire product lifecycle from concept design to mass manufacturing.Our in-house R&D team develops specialized GPS tracking, IOT , IIOT devices that withstand the harshest operational environments, including extreme temperatures, constant vibration, water immersion, and dust exposure. The E-Rutu vehicle tracker exemplifies our design philosophy—integrating 4G/2G cellular, Bluetooth LE mesh, temperature sensors, and door monitoring in a compact IP67-rated enclosure that survives automotive underhood conditions.\n\nOur production flexibility accommodates custom configurations, from white-label branding to specialized sensor arrays and unique form factors. The Bluetooth LE sensors demonstrates our wireless expertise, delivering 220-meters to 1000 meters range and multi-year battery life for warehouse industrial and open environments  monitoring applications.\n\nQuality assurance includes accelerated life testing, and real-world field trials before any product reaches your fleet. With FOTA infrastructure built into every device, hardware deployed today receives tomorrow's features through automatic updates. For OEM partners, we offer complete white-label programs including custom firmware, branded enclosures, and packaging that presents our technology as your proprietary solution.",
      },
      {
        slug: 'mobile-application',
        label: 'SOFTWARE DEVELOPMENT',
        heading: 'Software Solutions',
        image: '/assets/images/support3.jpg',
        fullContent:
          'SiWi develops native mobile applications for iOS and Android that extend platform capabilities to field personnel and mobile managers. The driver application provides turn-by-turn navigation optimized for commercial vehicles, electronic logging for hours-of-service compliance, and digital proof-of-delivery capture including signatures, photos, and barcode scans. Real-time synchronization ensures dispatchers receive status updates instantly, while offline functionality maintains productivity in cellular dead zones with automatic queue-and-forward when connectivity restores.\n\nThe manager application delivers dashboard analytics, alert acknowledgment, and remote configuration capabilities to smartphones and tablets. Geofenced arrival notifications trigger customer communications automatically, while chat functionality connects drivers with dispatch without external messaging apps. White-label development presents your brand identity through custom themes, logos, and app store listings that appear as your proprietary technology.\n\nOur development process emphasizes usability testing with actual field personnel, ensuring interfaces remain intuitive despite rugged conditions and glove operation. Regular updates introduce new features while maintaining backward compatibility with older device generations. Enterprise mobility management integration supports MDM deployment, remote wipe capabilities, and corporate app store distribution for organizations with strict IT security policies.',
      },
      {
        slug: 'support-consulting',
        label: 'SUPPORT & CONSULTING',
        heading: 'Expert Support',
        image: '/assets/images/support.jpg',
        fullContent:
          "SiWi's customer success organization delivers comprehensive support spanning technical assistance, strategic consulting, and training programs. Our technical support center operates 24/7/365 with multilingual engineers available via phone, email, live chat, and remote diagnostics tools that accelerate issue resolution. Enterprise clients receive dedicated account managers who understand their operational context and provide proactive guidance on optimization opportunities.\n\nImplementation consulting ensures successful deployment through site surveys, network planning, installation supervision, and configuration validation. For complex integrations, our solutions architects collaborate with your IT team to design robust architectures that meet security, scalability, and performance requirements. Regulatory consulting helps navigate industry-specific compliance including HACCP for food safety, GDP for pharmaceuticals, and DOT regulations for transportation.\n\nTraining programs range from self-paced online modules to instructor-led sessions delivered virtually or on-site. Certification tracks develop internal expertise for system administration, integration development, and train-the-trainer programs. Documentation includes comprehensive user guides, API references, troubleshooting playbooks, and video tutorials accessible through our knowledge base. With SLA-backed response times, proactive health monitoring, and continuous improvement programs, we ensure your SiWi investment delivers maximum value throughout its operational life.",
      },
    ],
    []
  );

  const { data: apiService, isLoading } = useQuery(
    ['service', slug],
    () => serviceService.getBySlug(slug),
    {
      enabled: !!slug,
      retry: 0,
    }
  );

  const service = apiService || fallbackSections.find((s) => s.slug === slug);

  // Customer Stories Data - All 3 services with their slugs for navigation
  const allCustomerStories = [
    {
      id: 0,
      slug: 'device-manufacturing',
      tabLabel: 'Device Manufacturing',
      title: 'Hardware Engineering Excellence',
      description: 'SiWi oversees complete hardware creation pipelines—from initial concept visualization through high-volume production runs. Our dedicated research division crafts ruggedized positioning hardware engineered to thrive in punishing conditions: severe thermal fluctuations, relentless mechanical vibration, complete liquid submersion, and particulate contamination. The flagship E-Rutu automotive tracker embodies our engineering ethos—combining dual-band cellular connectivity (4G/2G), Bluetooth Low Energy 5.1 mesh networking, environmental temperature monitoring, and cargo access detection within a miniature IP67-certified housing built to endure engine-bay deployment.',
      image: '/assets/images/customer3.jpg',
    },
    {
      id: 1,
      slug: 'mobile-application',
      tabLabel: 'SOFTWARE DEVELOPMENT',
      title: 'Smart Mobility Solutions',
      description: 'SiWi engineers native smartphone solutions for both iOS and Android ecosystems, empowering remote workforces and traveling executives with full platform functionality. Our driver-centric interface delivers commercial-grade turn-by-turn routing, automated hours-of-service digital logging, and comprehensive delivery verification through signature capture, photographic documentation, and barcode scanning. Instantaneous data synchronization keeps dispatch hubs continuously informed, while robust offline architecture guarantees uninterrupted operation regardless of connectivity gaps.',
      image: '/assets/images/customer2.jpg',
    },
    {
      id: 2,
      slug: 'support-consulting',
      tabLabel: 'SUPPORT & CONSULTING',
      title: 'Client Success Ecosystem',
      description: "SiWi's customer enablement division provides end-to-end assistance encompassing technical troubleshooting, operational strategy advisory, and competency-building curricula. Our global support nerve center functions continuously without interruption, staffed by polyglot specialists reachable through voice, electronic messaging, real-time text exchange, and sophisticated remote diagnostic platforms that compress resolution timelines. Major accounts benefit from assigned relationship stewards possessing deep familiarity with their specific operational landscapes, delivering anticipatory recommendations for performance enhancement and efficiency gains.",
      image: '/assets/images/customer1.jpg',
    }
  ];

  // Filter out the current service - show only the other 2 tabs
  const customerStories = useMemo(() => {
    return allCustomerStories.filter(story => story.slug !== slug);
  }, [slug]);

  // Reset active tab when slug changes (since we only have 2 tabs now)
  useEffect(() => {
    setActiveTab(0);
  }, [slug]);

  // Icon rotation effect - 5 seconds per icon
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIcon((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (service && contentRef.current) {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
      );
    }
  }, [service]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen pt-32 text-center px-4">
        <h1 className="text-2xl font-bold text-slate-900">Service not found</h1>
        <Link
          to="/services"
          className="group inline-flex items-center text-indigo-600 font-semibold mt-4"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back to Services
        </Link>
      </div>
    );
  }

  const label = service.label || 'SERVICE';
  const heading = service.heading || service.title || 'Service';
  const image = service.image || service.coverImage;

  const fullText =
    service.fullContent ||
    service.description ||
    service.shortDescription ||
    '';

  const paragraphs = fullText
    .split('\n')
    .map((p) => p.trim())
    .filter(Boolean);

  const icons = [
    { Icon: Cpu, label: 'Hardware Solutions' },
    { Icon: Smartphone, label: 'Mobile Apps' },
    { Icon: HeadphonesIcon, label: 'Expert Support' }
  ];

  return (
    <div className="min-h-screen pt-20 bg-white" ref={contentRef}>
      
      {/* Back to Services - Own Line */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[30px]">
        <Link
          to="/services"
          className="group inline-flex items-center text-slate-500 hover:text-indigo-600 font-medium transition-colors"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back to Services
        </Link>
      </div>

      {/* Centered Label - Professional Indigo Color */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 text-center">
        <p className="text-3xl sm:text-4xl md:text-[50px] font-bold uppercase tracking-wide text-indigo-900 font-sans">
          {label}
        </p>
      </div>

      {/* Center Heading - Sophisticated Slate */}
      <div className="text-center mt-4 px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-800 font-sans">
          {heading}
        </h1>
      </div>

      {/* FULL IMAGE */}
      {image ? (
        <div className="w-full mt-[20px]">
          <img
            src={image}
            alt={heading}
            className="w-full h-[500px] object-contain"
          />
        </div>
      ) : null}

      {/* Full Content - Increased Width and Text Size */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        {paragraphs.map((p, idx) => (
          <p
            key={idx}
            className="text-slate-600 text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 font-light"
          >
            {p}
          </p>
        ))}
      </div>

      {/* CTA Section - Increased Height with Centered Content */}
      <div className="w-full relative overflow-hidden">
        {/* Light gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-200/40 via-slate-200/50 to-teal-200/40" />
        
        {/* Darker gray overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-300/30 via-gray-300/40 to-slate-400/50" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          {/* Items centered with minimal gap */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
            
            {/* Left Side - Text Content - Increased size */}
            <div className="flex-1 text-left lg:pr-8">
              <h2 className="text-5xl sm:text-6xl font-bold text-slate-900 mb-3 font-sans tracking-tight leading-none">
                SRISHTI WIRELESS <br />
                <span className="text-teal-700">Solutions</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 font-light tracking-wide">
                Transform your operations with SiWi's comprehensive IoT solutions. Schedule a consultation to discuss your specific requirements and discover how our expertise drives measurable results.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold text-white bg-teal-700 border-2 border-teal-700 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all duration-300 uppercase tracking-wider"
              >
                Contact Us
              </Link>
            </div>

            {/* Right Side - Icon with Label on Image - Increased size */}
            <div className="flex-1 flex items-center justify-center lg:pl-8">
              <div className="relative w-[500px] h-[350px] flex items-center justify-center overflow-hidden">
                {[
                  { src: '/assets/images/serviceicon1.png', name: 'Hardware Solutions' },
                  { src: '/assets/images/serviceicon3.png', name: 'Mobile Apps' },
                  { src: '/assets/images/serviceicon2.png', name: 'Expert Support' }
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`absolute transition-all duration-1000 ease-in-out transform flex flex-col items-center justify-center ${
                      index === currentIcon 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-75'
                    }`}
                  >
                    {/* Icon container with label on top - Increased size */}
                    <div className="relative w-[450px] h-[320px]">
                      {/* Label written on icon - Increased text */}
                      <span className="absolute top-4 left-0 right-0 text-4xl font-bold text-slate-800 tracking-wide leading-none text-center z-10 drop-shadow-lg">
                        {item.name}
                      </span>
                      {/* Icon image - Increased size */}
                      <img 
                        src={item.src} 
                        alt={item.name}
                        className="w-full h-full object-contain drop-shadow-xl"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Customer Stories Section - Shows only 2 other services */}
      <div className="w-full bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header - Centered (Same for all pages) */}
          <div className="mb-8 text-center">
            <p className="text-base sm:text-lg font-semibold text-teal-700 uppercase tracking-wider mb-4">
              CUSTOMER SUMMARY
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-slate-800 leading-tight max-w-4xl mx-auto">
              Our customers are powered by confidence that rises to the challenge
            </h2>
          </div>

          {/* Description Text - Centered (Same for all pages) */}
          <p className="text-slate-600 text-lg sm:text-xl md:text-2xl leading-relaxed mb-12 max-w-4xl mx-auto text-center">
            Every day, SiWi customers are advancing the potential of technology to connect people, data and workflows. Learn how they're achieving remarkable success through the power of the SiWi ecosystem.
          </p>

          {/* Tabs Navigation - Centered (Only 2 tabs based on current page) */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex flex-wrap justify-center gap-0">
              {customerStories.map((story, index) => (
                <button
                  key={story.id}
                  onClick={() => setActiveTab(index)}
                  className={`px-6 py-4 text-sm sm:text-base font-semibold uppercase tracking-wider transition-colors duration-200 border-b-2 ${
                    activeTab === index
                      ? 'border-teal-700 text-teal-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {story.tabLabel}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Text Content */}
            <div className="space-y-6 text-center lg:text-left">
              <h3 className="text-3xl sm:text-4xl font-semibold text-slate-800">
                {customerStories[activeTab]?.title}
              </h3>
              <p className="text-slate-600 text-lg sm:text-xl leading-relaxed">
                {customerStories[activeTab]?.description}
              </p>
              {/* Read More Link - Navigates to that service detail page */}
              <Link
                to={`/services/${customerStories[activeTab]?.slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center justify-center px-8 py-4 text-base  font-bold text-black bg-gray-200 hover:text-white hover:bg-gray-600 transition-colors duration-300 uppercase tracking-wider "
              >
                Read More
              </Link>
            </div>

            {/* Right Side - Full Image No Container */}
            <div className="relative w-full h-[600px]">
              <img
                src={customerStories[activeTab]?.image}
                alt={customerStories[activeTab]?.title}
                className="w-full h-full object-cover"
              />
            </div>

          </div>

        </div>
      </div>

    </div>
  );
};

export default ServiceDetail;